import { useState, useEffect, useCallback, type FormEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts";
import { apiPost } from "../../lib/api";
import { Button } from "../Button";
import { AuthInput } from "./AuthInput";
import { ReferralCodeInput } from "../referrals/ReferralCodeInput";
import type { ReferralValidation } from "../../hooks/useReferrals";

// localStorage keys for referral persistence
const REFERRAL_CODE_KEY = "jumpbean_referral_code";
const REFERRAL_VALIDATION_KEY = "jumpbean_referral_validation";

interface RegisterFormProps {
  onSuccess?: () => void;
}

/**
 * RegisterForm - Email/password registration form with Supabase integration.
 * Includes password confirmation, referral code input, and friendly validation.
 */
export function RegisterForm({ onSuccess }: RegisterFormProps) {
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    form?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Referral code state
  const [referralCode, setReferralCode] = useState("");
  const [referralValidation, setReferralValidation] = useState<ReferralValidation | null>(null);
  const [showReferralInput, setShowReferralInput] = useState(false);

  // Check URL params and localStorage for referral code on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refFromUrl = params.get("ref");

    if (refFromUrl) {
      // URL param takes priority
      setReferralCode(refFromUrl.toUpperCase());
      setShowReferralInput(true);
      validateReferralCode(refFromUrl.toUpperCase());
    } else {
      // Check localStorage for persisted code
      const storedCode = localStorage.getItem(REFERRAL_CODE_KEY);
      const storedValidation = localStorage.getItem(REFERRAL_VALIDATION_KEY);

      if (storedCode) {
        setReferralCode(storedCode);
        setShowReferralInput(true);

        if (storedValidation) {
          try {
            const parsed = JSON.parse(storedValidation) as ReferralValidation;
            setReferralValidation(parsed);
          } catch {
            // Invalid stored validation, re-validate
            validateReferralCode(storedCode);
          }
        } else {
          validateReferralCode(storedCode);
        }
      }
    }
  }, [location.search]);

  // Persist referral code to localStorage when it changes
  useEffect(() => {
    if (referralCode && referralValidation?.valid) {
      localStorage.setItem(REFERRAL_CODE_KEY, referralCode);
      localStorage.setItem(REFERRAL_VALIDATION_KEY, JSON.stringify(referralValidation));
    }
  }, [referralCode, referralValidation]);

  const validateReferralCode = useCallback(async (code: string) => {
    if (!code.trim()) {
      setReferralValidation(null);
      return;
    }

    try {
      const result = await apiPost<ReferralValidation, { code: string }>(
        "/api/referrals/validate",
        { code: code.trim().toUpperCase() }
      );

      if (result.data) {
        setReferralValidation(result.data);
      } else {
        setReferralValidation({
          valid: false,
          code: code,
          reason: result.error?.message || "Failed to validate code",
        });
      }
    } catch {
      setReferralValidation({
        valid: false,
        code: code,
        reason: "Failed to validate code. Please try again.",
      });
    }
  }, []);

  const handleReferralCodeChange = useCallback((value: string) => {
    setReferralCode(value);
    // Reset validation when code changes
    if (referralValidation && value !== referralValidation.code) {
      setReferralValidation(null);
    }
  }, [referralValidation]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!email) {
      newErrors.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Please create a password";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords don't match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) return;

    setIsSubmitting(true);

    const { error } = await register(email, password);

    if (error) {
      setIsSubmitting(false);
      // Map Supabase error messages to user-friendly messages
      let friendlyMessage = "Something went wrong. Please try again.";

      if (error.message?.includes("already registered")) {
        friendlyMessage = "This email is already registered. Try logging in instead.";
      } else if (error.message?.includes("Password")) {
        friendlyMessage = error.message;
      } else if (error.message?.includes("valid email")) {
        friendlyMessage = "Please enter a valid email address.";
      }

      setErrors({ form: friendlyMessage });
      return;
    }

    // Apply referral code if valid
    if (referralCode && referralValidation?.valid) {
      try {
        await apiPost("/api/referrals/apply", { code: referralCode });
        // Clear localStorage after successful application
        localStorage.removeItem(REFERRAL_CODE_KEY);
        localStorage.removeItem(REFERRAL_VALIDATION_KEY);
      } catch (applyError) {
        // Log but don't block registration
        console.error("Failed to apply referral code:", applyError);
        // Keep the code in localStorage for retry on next page load
      }
    }

    setIsSubmitting(false);

    // Show success message (email confirmation may be required)
    setShowSuccess(true);
    onSuccess?.();
  };

  // Success state after registration
  if (showSuccess) {
    return (
      <div className="text-center space-y-6">
        <div className="text-6xl animate-bounce">🎉</div>
        <h2 className="font-display font-bold text-display-sm text-meadow-600">
          Almost There!
        </h2>
        <p className="font-body text-body-lg text-neutral-600">
          We sent a confirmation email to <strong className="text-magic-600">{email}</strong>.
          Please check your inbox and click the link to activate your account.
        </p>
        {referralValidation?.valid && (
          <div className="p-4 bg-sunshine-50 border-2 border-sunshine-300 rounded-xl">
            <p className="font-body text-body-md text-neutral-700">
              Your referral code has been applied! You'll get{" "}
              <span className="font-semibold text-sunshine-700">1 free month</span> when you subscribe.
            </p>
          </div>
        )}
        <div className="pt-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/login")}
            fullWidth
          >
            Go to Login
          </Button>
        </div>
        <p className="font-body text-body-sm text-neutral-500">
          Didn't receive the email? Check your spam folder or{" "}
          <button
            type="button"
            onClick={() => setShowSuccess(false)}
            className="text-magic-600 hover:text-magic-700 transition-colors"
          >
            try again
          </button>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {/* Referral reward preview banner */}
      {referralValidation?.valid && (
        <div
          className="
            p-4 rounded-xl
            bg-gradient-to-r from-sunshine-50 via-meadow-50 to-sunshine-50
            border-2 border-sunshine-300
            animate-[slideDown_0.3s_ease-out]
          "
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🎉</span>
            <div>
              <p className="font-display font-bold text-body-md text-neutral-800">
                Referral code applied: {referralCode}
              </p>
              <p className="font-body text-body-sm text-neutral-600">
                You'll get <span className="font-semibold text-meadow-600">1 free month</span> when you subscribe!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form error message */}
      {errors.form && (
        <div
          className="
            p-4 rounded-xl
            bg-fairy-50 border-2 border-fairy-200
            text-fairy-700 font-body text-body-md
            flex items-start gap-3
          "
          role="alert"
        >
          <span className="text-xl flex-shrink-0">😕</span>
          <span>{errors.form}</span>
        </div>
      )}

      <AuthInput
        id="email"
        type="email"
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="parent@example.com"
        error={errors.email}
        autoComplete="email"
        autoFocus
        disabled={isSubmitting || isLoading}
      />

      <AuthInput
        id="password"
        type="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Create a password (8+ characters)"
        error={errors.password}
        autoComplete="new-password"
        minLength={8}
        disabled={isSubmitting || isLoading}
      />

      <AuthInput
        id="confirmPassword"
        type="password"
        label="Confirm Password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder="Type your password again"
        error={errors.confirmPassword}
        autoComplete="new-password"
        minLength={8}
        disabled={isSubmitting || isLoading}
      />

      {/* Referral code section */}
      {showReferralInput ? (
        <ReferralCodeInput
          value={referralCode}
          onChange={handleReferralCodeChange}
          isValid={referralValidation?.valid ?? null}
          validationMessage={referralValidation?.valid === false ? referralValidation.reason : undefined}
          referrerName={referralValidation?.referrerName}
          onValidate={validateReferralCode}
          disabled={isSubmitting || isLoading}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowReferralInput(true)}
          className="
            w-full py-3
            font-body text-body-md text-magic-600
            hover:text-magic-700
            transition-colors duration-200
            flex items-center justify-center gap-2
          "
        >
          <GiftIcon className="w-5 h-5" />
          Have a referral code?
        </button>
      )}

      <Button
        type="submit"
        variant="primary"
        fullWidth
        size="lg"
        isLoading={isSubmitting || isLoading}
        disabled={isSubmitting || isLoading}
      >
        Create Account
      </Button>

      <p className="font-body text-body-md text-neutral-600 text-center">
        Already have an account?{" "}
        <Link
          to="/login"
          className="text-magic-600 font-semibold hover:text-magic-700 transition-colors"
        >
          Log in
        </Link>
      </p>
    </form>
  );
}

function GiftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
      />
    </svg>
  );
}

export default RegisterForm;
